/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

import type { ApMountType, PlacedAp } from './types'

export type ApMountPreset = {
  id: ApMountType
  label: string
  defaultHeightM: number
  description: string
  /** Fixed EIRP offset vs ceiling omni (dB). */
  baselineDb: number
}

export const AP_MOUNT_PRESETS: ApMountPreset[] = [
  {
    id: 'ceiling',
    label: 'Ceiling',
    defaultHeightM: 3,
    description: 'Omni downward — default enterprise AP',
    baselineDb: 0,
  },
  {
    id: 'wall',
    label: 'Wall',
    defaultHeightM: 2.5,
    description: 'Directional — stronger in front, weaker behind',
    baselineDb: 0,
  },
  {
    id: 'desk',
    label: 'Desk / femto',
    defaultHeightM: 1.2,
    description: 'Low height, shorter usable range',
    baselineDb: -4,
  },
  {
    id: 'outdoor',
    label: 'Outdoor / pole',
    defaultHeightM: 4,
    description: 'Elevated omni — slightly extended horizontal reach',
    baselineDb: 2,
  },
]

export function defaultMountHeight(mount?: ApMountType): number {
  return AP_MOUNT_PRESETS.find(p => p.id === mount)?.defaultHeightM ?? 3
}

export function normalizeMountType(v: unknown): ApMountType {
  const s = String(v ?? 'ceiling').toLowerCase()
  if (s === 'wall' || s === 'desk' || s === 'outdoor' || s === 'ceiling') return s
  return 'ceiling'
}

/**
 * Mount & height adjustment added to RSSI (dB) at sample point.
 * Wall mount uses 2D orientation (deg, 0 = east, 90 = south on floor plan).
 */
export function mountRfAdjustDb(
  ap: Pick<PlacedAp, 'x' | 'y' | 'mount' | 'mountOrientationDeg' | 'mountHeightM'>,
  px: number,
  py: number,
  horizDistM: number,
): number {
  const mount = normalizeMountType(ap.mount)
  const preset = AP_MOUNT_PRESETS.find(p => p.id === mount)!
  let adj = preset.baselineDb

  const clientHeightM = 1.5
  const apH = ap.mountHeightM ?? preset.defaultHeightM
  const dist3d = Math.hypot(horizDistM, Math.max(0, apH - clientHeightM))
  if (horizDistM > 0.5 && dist3d > horizDistM) {
    adj -= Math.min(3, (dist3d - horizDistM) * 0.4)
  }

  if (mount === 'wall') {
    const orient = ap.mountOrientationDeg ?? 0
    const angleToPoint = (Math.atan2(py - ap.y, px - ap.x) * 180) / Math.PI
    let delta = Math.abs(angleToPoint - orient)
    while (delta > 180) delta = 360 - delta
    if (delta <= 55) adj += 3
    else if (delta <= 105) adj -= 1
    else adj -= 7
  }

  if (mount === 'desk' && horizDistM > 12) {
    adj -= Math.min(6, (horizDistM - 12) * 0.35)
  }

  return adj
}

export function mountLabel(mount?: ApMountType): string {
  return AP_MOUNT_PRESETS.find(p => p.id === mount)?.label ?? 'Ceiling'
}
